import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Container from '@mui/material/Container';
import { useNavigate, useSearchParams } from 'react-router-dom';
import MKBox from 'components/MaterialKit/MKBox';
import { useAuth } from 'contexts/auth';
import { getStoredAuth } from 'api';
import { useApp } from 'contexts/app';
import { login } from 'api/auth';
import BuddhistLoginForm from 'components/BuddhistLoginForm';
import { getLocaleMap } from 'utils/locales';

const loginFormInitialValues = {
  username: '',
  password: '',
};

const BuddhistLoginSection = ({
  app_id,
  button_color,
  button_title,
  show_sign_up,
  show_forgot_password,
  text_color,
  navigate_to,
  forgot_password_url,
  register_url,
  username_label,
  password_label,
  title,
  login_label,
  dont_have_account_label,
  sign_up_now_label,
  show_sign_up_login_signup_via_others,
  login_signup_via_others_label,
  forgot_password_label,
  login_as_admin_label,
  show_login_as_admin,
  login_as_user_label,
  ...props }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setAuth } = useAuth();
  const { app } = useApp();
  const appId = app_id || app?.app_id;

  const [lm, setLm] = useState({});
  useEffect(() => {
    getLocaleMap(['login_title', 'username', 'please_enter_password', 'forgot_password',
      'login', 'login_as_admin', 'login_as_member',
      'dont_have_account', 'sign_up_now', 'login_signup_via_third_party'])
      .then((response) => {
        setLm(response);
      });
  }, []);

  const onLoginToApi = useCallback((values, { setFieldError }) => {
    return login(values.username, values.password)
      .then(({ data: user }) => {
        if (user?.app !== appId) {
          setFieldError('form', 'Invalid app');
          return;
        }
        const newAuth = { ...getStoredAuth(), user };
        setAuth(newAuth);
        if (searchParams.get('redirect')) {
          navigate(searchParams.get('redirect'));
        } else {
          navigate(navigate_to || '/');
        }
      })
      .catch((err) => {
        setFieldError('form', err.message);
      });
  }, [appId, setAuth, searchParams, navigate, navigate_to]);

  return (
    <Container>
      <MKBox py="2rem">
        <BuddhistLoginForm
          onLogin={onLoginToApi}
          initialValues={loginFormInitialValues}
          buttonColor={button_color}
          buttonTitle={button_title}
          showSignUp={show_sign_up}
          showForgotPassword={show_forgot_password}
          textColor={text_color}
          forgotPasswordUrl={forgot_password_url}
          registerUrl={register_url}
          passwordLabel={lm.please_enter_password || password_label}
          usernameLabel={lm.username || username_label}
          title={lm.login_title || title}
          loginLabel={lm.login || login_label}
          dontHaveAccountLabel={lm.dont_have_account || dont_have_account_label}
          signUpNowLabel={lm.sign_up_now || sign_up_now_label}
          showSignUpLoginSignupViaOthers={show_sign_up_login_signup_via_others}
          loginSignupViaOthersLabel={lm.login_signup_via_third_party || login_signup_via_others_label}
          forgotPasswordLabel={lm.forgot_password || forgot_password_label}
          loginAsAdminLabel={lm.login_as_admin || login_as_admin_label}
          showLoginAsAdmin={show_login_as_admin}
          loginAsUserLabel={lm.login_as_member || login_as_user_label}
          {...props}
        />
      </MKBox>
    </Container>
  );
};

BuddhistLoginSection.propTypes = {
  show_sign_up: PropTypes.bool,
  show_forgot_password: PropTypes.bool,
  button_title: PropTypes.string,
  button_color: PropTypes.string,
  username_label: PropTypes.string,
  password_label: PropTypes.string,
  text_color: PropTypes.string,
  navigate_to: PropTypes.string,
  forgot_password_url: PropTypes.string,
  register_url: PropTypes.string,
  app_id: PropTypes.string,
  title: PropTypes.string,
  login_label: PropTypes.string,
  dont_have_account_label: PropTypes.string,
  sign_up_now_label: PropTypes.string,
  show_sign_up_login_signup_via_others: PropTypes.bool,
  login_signup_via_others_label: PropTypes.string,
  forgot_password_label: PropTypes.string,
  login_as_admin_label: PropTypes.string,
  show_login_as_admin: PropTypes.bool,
  login_as_user_label: PropTypes.string,
};

BuddhistLoginSection.defaultProps = {
  show_sign_up: true,
  show_forgot_password: false,
  navigate_to: null,
  forgot_password_url: null,
  register_url: null,
};

export default BuddhistLoginSection;
